import React, { useEffect, useRef, useState } from 'react';
import { tw } from 'twind';

import { useFeature } from 'hooks/useFeature';
import { useTranslate } from 'hooks/useTranslations';
import { useAmazon } from 'hooks/useAmazon';
import { useSiteConfig } from 'hooks/useSiteConfig';
import { isServer, PaymentMethod } from 'utils/constants';
import { paymentClickEvent } from 'utils/analytics';

export const AmazonPayButton = ({ disabled }: { disabled?: boolean }) => {
  const t = useTranslate();
  const { env, localizeUrlPath, amazonPayButtonOptions } = useSiteConfig();
  const isMountRender = useRef(true);
  const isAmazonPayEnabled = useFeature(PaymentMethod.AMAZON_PAY);
  const [isScriptLoaded, setIsScriptLoaded] = useState(false);
  const { loadAmazonScript } = useAmazon();

  useEffect(() => {
    const amazonPayButton = document.getElementById('AmazonPayButton');
    if (!isAmazonPayEnabled || !amazonPayButton) return;

    if (!isServer && isMountRender.current && window?.OffAmazonPayments) {
      isMountRender.current = false;
      const params = {
        type: 'PwA',
        color: amazonPayButtonOptions?.color,
        size: amazonPayButtonOptions?.size,
        useAmazonAddressBook: true,
        authorization: () => {
          const loginOptions = {
            scope: env.amazonPayScope as string,
            popup: true,
          };

          window?.amazon?.Login.authorize(
            loginOptions,
            localizeUrlPath('/amazonpay/redirect')
          );
        },
      };

      window?.OffAmazonPayments.Button(
        'AmazonPayButton',
        env.amazonPayMerchantId as string,
        params
      );
    }
  }, [
    amazonPayButtonOptions?.color,
    amazonPayButtonOptions?.size,
    env.amazonPayMerchantId,
    env.amazonPayScope,
    isAmazonPayEnabled,
    isScriptLoaded,
    localizeUrlPath,
  ]);

  if (!isAmazonPayEnabled) return null;

  loadAmazonScript(setIsScriptLoaded);

  return (
    <>
      <div
        id="AmazonPayButton"
        className={tw(
          'flex justify-center items-center w-full',
          disabled ? 'pointer-events-none opacity-40' : ''
        )}
        onClick={() => paymentClickEvent()}
      />
      <p
        className="flex justify-center items-center w-full text-xs"
        data-test-id="amazon-pay-button-note"
      >
        {t('amazonPayButtonNote')}
      </p>
    </>
  );
};
